/* eslint-disable jsdoc/require-param-type */

import {
  put, call, takeLatest, select,
} from 'redux-saga/effects';

import {
  MODALS, API_URLS, FETCH_OPTIONS,
} from '../constants';
import { requestGet } from '../utils/request';
import { aGetHistoricalEventsAction } from '../actions/historicalEventsActions';
import { getTimeFromEvent } from '../utils/common';
import { getSoccerHistoricalEvents } from '../selectors/historicalEvents';
import { aSetIsFetching, aSetOpenModal } from '../reducers/common';
import { aGetHistoricalEventsSuccess } from '../reducers/historicalEvents';

/**
 * group historical events based on the startdate hour
 *
 * @param {object} events
 * @returns {object[]} array of objects that has the hour and events for that hour
 *
 * ex. [
    {
      hour: 1:00,
      events: { //data }
    }
 * ]
 */
function groupEventsByHour(events) {
  const eventsCopy = [...events];

  const eventsByHourObject = eventsCopy
    .reduce((acc, curr) => {
      const dateTime = new Date(curr.startDateTime);
      dateTime.setHours(dateTime.getHours() + 1);
      const time = getTimeFromEvent(dateTime.toISOString());
      const hour = parseInt(time.split(' ')[0]);
      if (acc[hour] === undefined) {
        const paddedHour = `${hour}`.padStart(2, '0');
        acc[hour] = {
          hour: `${paddedHour}:00`,
          events: [curr],
        };

        return acc;
      }

      acc[hour].events.push(curr);
      return acc;
    }, {});

  return Object.keys(eventsByHourObject).map((key) => ({
    hour: eventsByHourObject[key].hour,
    events: eventsByHourObject[key].events,
  }));
}
/**
 *
 * @param action
 * @memberof sagas/historicalEvents
 * @async
 * @returns {void}
 */
function* getHistoricalEventsSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_HISTORICAL_EVENTS,
      value: true,
    }));

    const response = yield call(
      requestGet,
      action.payload.queryParams ? `${API_URLS.GET_HISTORICAL_EVENTS}${action.payload.queryParams}` : API_URLS.GET_HISTORICAL_EVENTS,
      true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    }

    const previousEvents = yield select(getSoccerHistoricalEvents);
    const groupedEvents = groupEventsByHour(response.data.events);

    const events = action.payload.loadMore ? [...previousEvents, ...groupedEvents] : groupedEvents;

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_HISTORICAL_EVENTS,
      value: false,
    }));

    yield put(aGetHistoricalEventsSuccess({
      events,
      lastEventId: response.data?.lastEventId,
      lastDate: response.data?.lastDate,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_HISTORICAL_EVENTS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/goldenGoalSettings
 * @async
 * @returns {void}
 */
export default function* historicalEventsSaga() {
  yield takeLatest(aGetHistoricalEventsAction, getHistoricalEventsSaga);
}

